import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  TextField,
  InputAdornment,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { api } from "../api/api";
import { Profile_Mob_Api, country_url } from "../api/urls";
import { MobileNumberDropdown } from "./MobileNumberDropdown";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { XIcon } from "lucide-react";

const EditMobilePopup = ({
  setPage,
  setOtpToken,
  page,
  onClose,
  setMobile,
  setCountryCode,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm();

  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState(91);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const { data } = await api.get(country_url);
        setCountries(data);
        const defaultCountryData = data.find(
          (country) => country.country_mobile_code === 91,
        );
        if (defaultCountryData) {
          //  setDefaultCountry(defaultCountryData);
          setSelectedCountryCode(defaultCountryData.country_mobile_code);
        }
      } catch (error) {
        toast.error("Failed to load countries");
      } finally {
        //  setIsCountriesLoading(false);
      }
    };

    fetchCountries();
  }, []);

  // ✅ Strip unwanted leading "0" or "91" like in Login
  useEffect(() => {
    const unsub = watch((value) => {
      const { mobile } = value;
      if (mobile?.startsWith("91")) {
        setValue("mobile", mobile.slice(2));
      } else if (mobile?.startsWith("0")) {
        setValue("mobile", mobile.slice(1));
      }
    });
    return () => unsub.unsubscribe();
  }, [watch, setValue]);

  const handleSubmitFn = async (formData) => {
    if (!formData.country_code) {
      toast.error("Please select a country code.");
      return;
    }

    setLoading(true);

    const payload = {
      mobile_number: formData.country_code + formData.mobile,
      country_code: formData.country_code,
    };

    try {
      const res = await api.post(Profile_Mob_Api, payload);
      if (res.status === 200) {
        const token = res.data.token;
        setOtpToken(token);
        setPage(1);
        toast.success("Mobile number updated successful");
        setMobile(formData.mobile);
        setCountryCode(formData.country_code);
      } else {
        toast.error("Unexpected error");
      }
    } catch (error) {
      console.error(error);

      const message = error.response?.data?.data;
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={page === 0} onClose={onClose} fullWidth maxWidth="xs">
      <DialogContent
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "30vh",
          backgroundColor: "#fff",
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          style={{
            backgroundColor: "#fff",
            // padding: "30px 20px",
            borderRadius: "12px",
            width: "100%",
            // maxWidth: "400px",
            boxShadow: "none",
          }}
          className="relative"
        >
          <div
            className="flex justify-end absolute -end-2 -top-10 cursor-pointer"
            onClick={onClose}
          >
            <XIcon />
          </div>
          <h2
            style={{
              textAlign: "center",
              fontWeight: "600",
              marginBottom: "20px",
              color: "#000",
            }}
          >
            Update Mobile Number
          </h2>

          <form onSubmit={handleSubmit(handleSubmitFn)}>
            <motion.div
              whileFocus={{ scale: 1.02 }}
              style={{ marginBottom: "20px" }}
            >
              <Controller
                name="mobile"
                control={control}
                rules={{
                  required: "Phone number is required",
                  pattern: {
                    value: /^[0-9]{10,15}$/,
                    message: "Enter a valid phone number",
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    placeholder="Phone No"
                    error={!!errors.mobile}
                    helperText={error ? error.message : ""}
                    fullWidth
                    autoComplete="tel-national"
                    InputProps={{
                      startAdornment: (
                        <Controller
                          name="country_code"
                          control={control}
                          defaultValue={selectedCountryCode}
                          rules={{ required: "Country code is required" }}
                          render={({ field }) => (
                            <InputAdornment position="start">
                              <MobileNumberDropdown
                                countryData={countries}
                                value={field.value}
                                onChange={(v) => {
                                  field.onChange(v.country_mobile_code);
                                  // setAlphaTwoCode(v.alpha_two_code.toLowerCase());
                                  setSelectedCountryCode(v.country_mobile_code);
                                }}
                              />
                            </InputAdornment>
                          )}
                        />
                      ),
                    }}
                    // ✅ prevent typing leading 0 or 91
                    onChange={(e) => {
                      let val = e.target.value;
                      if (val.startsWith("0")) val = val.slice(1);
                      if (val.startsWith("91")) val = val.slice(2);
                      field.onChange(val);
                    }}
                  />
                )}
              />
            </motion.div>

            <motion.button
              type="submit"
              disabled={loading}
              whileTap={{ scale: 0.95 }}
              whileHover={{ scale: 1.02 }}
              style={{
                width: "100%",
                padding: "12px",
                backgroundColor: "#000",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "16px",
                fontWeight: "bold",
              }}
            >
              {loading ? "Processing..." : "Continue"}
            </motion.button>
          </form>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
};

export default EditMobilePopup;
